import {useEffect} from "react";
import {createWsSynchronizer} from "tinybase/synchronizers/synchronizer-ws-client/with-schemas";
import type {MergeableStore} from "tinybase/with-schemas";
import {create} from "zustand";
import {getStorageKey, storageWrapper} from "../storage";
import useIsOnline from "../utils/use-is-online";
import {SYNC_REQUEST_TIMEOUT_SECONDS} from "./sync-timeout";

type SingleConnectionState = {
  connectionCount: number;
  lastUpdate: Date | null;
  status: 0 | 1 | 2;
};

export type ConnectionState = {
  [key: string]: SingleConnectionState | undefined;
};

const updateKey = (key: string, connectionDiff: number, doUpdate: boolean) => {
  const now = new Date();
  if (doUpdate) {
    storageWrapper.storageSet(getStorageKey.connectionState(key), now.getTime());
  }
  useConnectionState.setState((prev) => ({
    [key]: {
      connectionCount: (prev[key]?.connectionCount ?? 0) + connectionDiff,
      lastUpdate: doUpdate ? now : (prev[key]?.lastUpdate ?? null),
      status: prev[key]?.status ?? 0,
    },
  }));
};

export const useConnectionState = create<ConnectionState>(() => ({}));

const createWs = async (url: string) => {
  return new Promise<WebSocket>((res, rej) => {
    const ws = new WebSocket(url);
    ws.addEventListener("open", () => res(ws));
    ws.addEventListener("error", () => rej(new Error("Failed to connect to server")));
  });
};

const createManager = (url: string, store: MergeableStore<[any, any]>, key: string) => {
  let unsubs: (() => void)[] = [];
  let stopped = false;
  let restartDelay = 1000 + Math.random() * 500;
  const exectueUnsubs = () => {
    unsubs.forEach((fn) => fn());
    unsubs = [];
  };
  const manager = {
    start: async () => {
      const ws = await createWs(url).catch(() => {
        restartDelay = Math.min(restartDelay * 2, 30 * 1000);
        restart();
      });
      if (!ws) return;
      // eslint-disable-next-line @typescript-eslint/unbound-method
      const send = ws.send;
      ws.send = (data: string) => {
        updateKey(key, 0, true);
        return send.call(ws, data);
      };
      restartDelay = 1000 + Math.random() * 500;
      ws.addEventListener("close", (ev) => {
        if (!stopped) console.warn("WS CLOSED", ev.code);
        restart();
      });
      ws.addEventListener("error", (ev) => {
        console.warn("WS ERRORRED", ev);
        restart();
      });
      updateKey(key, 1, false);
      const synchronizer = await createWsSynchronizer(
        store,
        ws,
        SYNC_REQUEST_TIMEOUT_SECONDS
      ).catch((e: unknown) => {
        // the socket closed before tinybase took it over; its close listener restarts
        console.warn("SYNC FAILED", e);
        updateKey(key, -1, false);
      });
      if (!synchronizer) return;
      const listenerId = synchronizer.addStatusListener((_, status) => {
        useConnectionState.setState((prev) => ({
          ...prev,
          [key]: {...(prev[key] as SingleConnectionState), status},
        }));
      });
      const cleanup = () => {
        ws.close(1000, "cancelled");
        synchronizer.delListener(listenerId);
        void synchronizer.destroy();
        updateKey(key, -1, false);
      };
      try {
        await synchronizer.startSync();
      } catch (e) {
        // closing the socket triggers a restart, unless it's closed already and did so
        console.warn("SYNC FAILED", e);
        cleanup();
        return;
      }
      unsubs.push(cleanup);
      // we should allow tinybase to finalize the sync before cancelling,
      // otherwise the sync server will not close the connection correctly
      if (stopped) return exectueUnsubs();
      // eslint-disable-next-line no-console
      console.log("🔄 SYNC ACTIVE");
      ws.addEventListener("message", () => updateKey(key, 0, true));
    },
    stop: () => {
      stopped = true;
      exectueUnsubs();
    },
  };
  const restart = () => {
    exectueUnsubs();
    if (stopped) return;
    const timeoutId = setTimeout(() => {
      exectueUnsubs();
      void manager.start();
    }, restartDelay);
    // console.log("retry connection in ", restartDelay);
    unsubs.push(() => clearTimeout(timeoutId));
  };
  void manager.start();
  return manager;
};

const keepSyncing = (url: string, store: MergeableStore<[any, any]>, key: string) => {
  const manager = createManager(url, store, key);
  return () => {
    manager.stop();
  };
};
const getLastUpdateTimeFromStorage = (key: string) => {
  const val = storageWrapper.storageGet(getStorageKey.connectionState(key));
  if (!val) return null;
  return new Date(val);
};

export const useSync = (opts: {
  store: MergeableStore<[any, any]>;
  url: string | null;
  key: string;
}) => {
  const {store, url, key} = opts;
  const isOnline = useIsOnline();
  useEffect(() => {
    useConnectionState.setState(() => ({
      [key]: {
        connectionCount: 0,
        lastUpdate: getLastUpdateTimeFromStorage(key),
        status: 0,
      },
    }));
    if (!url || !isOnline) return;
    return keepSyncing(url, store, key);
  }, [url, isOnline, store, key]);
};
